import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import { LogOut, Sun, Moon, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import TenantSwitcher from './TenantSwitcher.jsx';
import { DASHBOARD_VERSION, DASHBOARD_BUILT } from '../version.js';
import { resolveTemplateKey, getTemplate, OWNER_SECTION } from '../lib/dashboardTemplates.js';

// The sidebar layout is now DATA-DRIVEN by the template registry
// (../lib/dashboardTemplates.js): a tenant's features resolve to a template key,
// and the template supplies its nav sections. The owner Admin section is appended
// only for templates that opt in (super-admin). See dashboardTemplates.js to edit
// which sections/items a layout shows.

// Persistent light/dark toggle. Reads the saved preference on first load
// (default = dark), then swaps the `light` class on <html> and writes back.
function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('alfred-theme') || 'dark';
  });
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('light', theme === 'light');
    window.localStorage.setItem('alfred-theme', theme);
  }, [theme]);
  return [theme, () => setTheme(theme === 'light' ? 'dark' : 'light')];
}

export default function DashboardLayout() {
  const { user, tenant, selectedTenant, isOwner, availableTenants, logout } = useAuth();
  // The switcher renders for owners with 2+ tenants; otherwise we show a static
  // label. For owners viewing another tenant, reflect that tenant's identity.
  const showSwitcher = isOwner && (availableTenants?.length || 0) >= 2;
  const shownTenant = (isOwner && selectedTenant) || tenant;
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  // The nav layout is resolved from the template registry: the tenant's features
  // (or the legacy executiveAdmin / dashboard='tasks' flags, or owner→super-admin)
  // pick a template, which supplies its sections. The owner Admin section is only
  // appended for templates that opt in (super-admin), so it stays hidden inside the
  // exec-admin / tasks-profile views even for the platform owner.
  const templateKey = resolveTemplateKey(shownTenant?.features, isOwner);
  const template = getTemplate(templateKey);
  const navSections = template.includesOwnerAdmin
    ? [...template.sections, OWNER_SECTION]
    : template.sections;

  const onLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Sidebar collapse. Persisted: re-collapsing on every page load would make the
  // wide tables it exists to widen snap back to narrow on each navigation.
  const [navCollapsed, setNavCollapsed] = useState(
    () => { try { return localStorage.getItem('alfred.navCollapsed') === '1'; } catch { return false; } }
  );
  const toggleNav = () => setNavCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('alfred.navCollapsed', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });

  return (
    <div className="min-h-screen flex">
      <aside
        className={`${navCollapsed ? 'w-16' : 'w-60'} shrink-0 border-r border-stone-900 bg-stone-950/60 ${navCollapsed ? 'px-2' : 'px-4'} py-6 hidden md:flex md:flex-col transition-[width] duration-150`}
      >
        {/* The toggle lives INSIDE the rail so it travels with it — collapsed, it is
            still the first thing under the mark, so there is always a way back. */}
        <button
          type="button"
          onClick={toggleNav}
          aria-label={navCollapsed ? 'Expand the menu' : 'Collapse the menu'}
          title={navCollapsed ? 'Expand the menu' : 'Collapse the menu'}
          className={`mb-4 flex items-center gap-2 rounded-lg px-2 py-1.5 text-stone-500 hover:text-alfred-gold ${navCollapsed ? 'justify-center' : 'self-end'}`}
        >
          {navCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
        <Link to="/" className={`flex items-center gap-2 mb-8 ${navCollapsed ? 'justify-center px-0' : 'px-2'}`}>
          <span className="text-alfred-gold font-serif text-xl">{navCollapsed ? 'A' : 'Alfred'}</span>
          {!navCollapsed && (
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">
              Voice
            </span>
          )}
        </Link>

        {/* Tenant switcher pinned to the TOP so owners don't scroll to switch. */}
        {showSwitcher && !navCollapsed && (
          <div className="mb-6">
            <div className="px-2 mb-1 text-[10px] font-medium uppercase tracking-[0.18em] text-stone-600">
              Tenant
            </div>
            <TenantSwitcher />
          </div>
        )}

        <nav className="flex-1 flex flex-col gap-5">
          {navSections.map((section) => (
            <div key={section.label} className="flex flex-col gap-1">
              {!navCollapsed && (
                <div className="px-3 mb-1 text-[10px] font-medium uppercase tracking-[0.18em] text-stone-600">
                  {section.label}
                </div>
              )}
              {section.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={navCollapsed ? label : undefined}
                  className={({ isActive }) =>
                    `${isActive ? 'nav-link-active' : 'nav-link'}${navCollapsed ? ' justify-center px-0' : ''}`
                  }
                >
                  <Icon size={16} />
                  {!navCollapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="mt-6 pt-6 border-t border-stone-900">
          {/* Switcher now lives at the TOP; here only the static tenant label for
              non-switcher (single-tenant / non-owner) users. */}
          {shownTenant && !showSwitcher && (
            <div className="px-2 mb-3">
              <div className="text-xs uppercase tracking-wider text-stone-500">
                Tenant
              </div>
              <div className="text-sm text-stone-200 truncate">
                {shownTenant.companyName || shownTenant.subdomain}
              </div>
              {shownTenant.plan && (
                <div className="text-xs text-stone-500 capitalize">
                  {shownTenant.plan} plan
                </div>
              )}
            </div>
          )}
          {user && !navCollapsed && (
            <div className="px-2 mb-3">
              <div className="text-xs text-stone-500 truncate">{user.email}</div>
            </div>
          )}
          <button
            onClick={toggleTheme}
            className={`nav-link w-full ${navCollapsed ? 'justify-center px-0' : 'justify-start'}`}
            type="button"
            title={navCollapsed ? (theme === 'light' ? 'Dark theme' : 'Light theme') : undefined}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            {!navCollapsed && <span>{theme === 'light' ? 'Dark theme' : 'Light theme'}</span>}
          </button>
          <button
            onClick={onLogout}
            className={`nav-link w-full ${navCollapsed ? 'justify-center px-0' : 'justify-start'}`}
            type="button"
            title={navCollapsed ? 'Sign out' : undefined}
          >
            <LogOut size={16} />
            {!navCollapsed && <span>Sign out</span>}
          </button>
          <div
            className={`mt-3 text-[10px] uppercase tracking-[0.18em] text-stone-600 ${navCollapsed ? 'text-center px-0' : 'px-2'}`}
            title={`Dashboard build ${DASHBOARD_VERSION} (${DASHBOARD_BUILT})`}
          >
            {navCollapsed ? `v${DASHBOARD_VERSION}` : `dashboard v${DASHBOARD_VERSION} · ${DASHBOARD_BUILT}`}
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
